import { css, SerializedStyles } from '@emotion/react';
import * as AIUtilities from '@mythos/game/AIUtilities';
import { GamePlayerViewModel, GameViewModel } from '@mythos/game/GameViewModel';
import { Resource, Token, TokenToDisplayName } from '@mythos/game/Resources';
import {
  computeBaseCounters,
  computePlayerFavorDetails,
  getMaxCounters,
  makeContextFromGame,
} from '@mythos/game/Rules';
import { makeMultiCardRenderer } from 'CardView';
import HoverCardStore from 'HoverCardStore';
import React from 'react';
import { AnimationRefSetter, PlayerAnimationRefs } from './AnimationHooks';
import { CardCountsView, CardCountTheme } from './CardCountsView';
import { UIColorPalette } from './ColorPalette';
import GameTimeView from './GameTimeView';
import { UserWithColor } from './GraphQLHelpers';
import Symbols, {
  BaseResourceToSymbol,
  ResourceToSymbol,
  SymbolView,
  TokenToSymbol,
} from './Symbols';

export type PlayerSummaryCountMode = 'counters' | 'total';
export type PlayerSummaryFavorMode = 'detailed' | 'total';
interface PlayerSummaryViewProps {
  player: GamePlayerViewModel;
  game: GameViewModel;
  user: UserWithColor;
  isWinning: boolean;
  isReady: boolean;

  countMode: PlayerSummaryCountMode;
  toggleCountMode: () => void;
  favorMode: PlayerSummaryFavorMode;
  toggleFavorMode: () => void;

  cardCountTheme: CardCountTheme;
  toggleCardCountTheme: () => void;

  animationRefSetter?: AnimationRefSetter;

  containerStyles?: {
    favor?: SerializedStyles;
    resources?: SerializedStyles;
    typeCounts?: SerializedStyles;
  };
}
export default function PlayerSummaryView(props: PlayerSummaryViewProps) {
  let { player, game, user, isWinning, isReady, animationRefSetter } = props;

  const context = makeContextFromGame(player, game);
  const production_counts = AIUtilities.totalProductionCounters(context);
  let { maxGold, maxMilitary } = getMaxCounters(context);

  const base_counts = computeBaseCounters(context);
  const hide_favor = game.options.hideFavor && !game.gameEndTimestamp;
  const { cardFavor, tributeFavor, prophecyFavor, prophecyCardIDs } =
    computePlayerFavorDetails(player, context);

  // adjust maxGold and maxMilitary to account for the player's base depending on countMode
  if (props.countMode === 'total') {
    maxGold += base_counts.gold;
    maxMilitary += base_counts.military;
  }

  let container_style: React.CSSProperties = {
    backgroundColor: isWinning
      ? UIColorPalette.playerSummaryWinnerBG
      : UIColorPalette.playerSummaryBG,
  };

  let ready_style = {
    display: 'inline-block',
    width: 9,
    backgroundColor: isReady
      ? UIColorPalette.playerSummaryStatusReady
      : UIColorPalette.playerSummaryStatusNotReady,
    height: '100%',
  };

  let profile_picture = (
    <img
      src={user.profilePictureURL}
      css={PlayerSummaryStyles.profilePicture}
    />
  );

  return (
    <div
      ref={(node) => {
        animationRefSetter?.(player.userID, PlayerAnimationRefs.summary, node);
      }}
      css={PlayerSummaryStyles.container}
      style={container_style}
    >
      <div style={ready_style} />
      <div css={PlayerSummaryStyles.topRow}>
        <div css={PlayerSummaryStyles.verticalStackItem}>
          <div css={PlayerSummaryStyles.inlineRow}>
            {profile_picture}
            <span
              css={PlayerSummaryStyles.name}
              style={{
                color: user.color,
              }}
            >
              {user.name}
            </span>
          </div>
          <PlayerGameTimeView
            css={PlayerSummaryStyles.time}
            game={game}
            player={player}
            isReady={isReady}
          />
        </div>
        <div css={PlayerSummaryStyles.verticalStackItem}>
          <div css={PlayerSummaryStyles.resourceWrapper}>
            {!hide_favor && (
              <PlayerFavorDisplay
                ref={(node) =>
                  animationRefSetter?.(
                    player.userID,
                    PlayerAnimationRefs.favor,
                    node,
                  )
                }
                tributeFavor={tributeFavor}
                cardFavor={cardFavor}
                eogFavor={prophecyFavor}
                favorMode={props.favorMode}
                toggleFavorMode={props.toggleFavorMode}
                onEnterAdditionalFavor={() => {
                  HoverCardStore.setCard(
                    prophecyCardIDs,
                    makeMultiCardRenderer(game.cardsByID),
                  );
                  HoverCardStore.setPosition('top-right-offset');
                }}
                onLeaveAdditionalFavor={() => {
                  HoverCardStore.setCard(null, null);
                }}
                css={props.containerStyles?.favor}
              />
            )}
            {hide_favor && (
              <>
                <PlayerTokenDisplay
                  token={Token.Tribute}
                  count={player.counters.tributeTokens}
                />
              </>
            )}
            {game.options.warTokens && (
              <PlayerTokenDisplay
                token={Token.War}
                count={player.counters.warTokens}
              />
            )}

            <div
              css={[
                PlayerSummaryStyles.productionWrapper,
                props.containerStyles?.resources,
              ]}
            >
              <PlayerResourceCounter
                resource="gold"
                tooltip="Gold"
                count={
                  props.countMode === 'counters'
                    ? player.counters.gold
                    : player.counters.gold + base_counts.gold
                }
                generateCount={production_counts.gold}
                baseCount={base_counts.gold}
                maxCount={maxGold}
                style={{ marginBottom: 4 }}
                countMode={props.countMode}
                toggleCountMode={props.toggleCountMode}
                ref={(node) =>
                  animationRefSetter?.(
                    player.userID,
                    PlayerAnimationRefs.gold,
                    node,
                  )
                }
              />
              <PlayerResourceCounter
                resource="military"
                tooltip="Military"
                count={
                  props.countMode === 'counters'
                    ? player.counters.military
                    : player.counters.military + base_counts.military
                }
                generateCount={production_counts.military}
                baseCount={base_counts.military}
                maxCount={maxMilitary}
                countMode={props.countMode}
                toggleCountMode={props.toggleCountMode}
                ref={(node) =>
                  animationRefSetter?.(
                    player.userID,
                    PlayerAnimationRefs.military,
                    node,
                  )
                }
              />
            </div>
          </div>
          <CardCountsView
            theme={props.cardCountTheme}
            player={player}
            game={game}
            css={props.containerStyles?.typeCounts}
            onClick={props.toggleCardCountTheme}
          />
        </div>
      </div>
    </div>
  );
}

const PlayerSummaryStyles = {
  container: css({
    display: 'flex',
    height: 65,
    padding: 3,
  }),
  topRow: css({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  }),
  verticalStack: css({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-center',
  }),
  verticalStackItem: css({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  }),
  inlineRow: css({
    display: 'inline-flex',
  }),
  resourceWrapper: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '65%',
    paddingLeft: 4,
  }),
  mobileResourceWrapper: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 6,
    flexGrow: 1,
  }),
  productionWrapper: css({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  }),
  name: css({
    display: 'inline-block',
    fontSize: 20,
    fontWeight: 'bold',
    maxWidth: 180,
    minWidth: 0,
    overflowX: 'clip',
    overflowY: 'visible',
    textOverflow: 'ellipsis',
  }),
  time: css({
    color: UIColorPalette.playerSummaryResourceTextLight,
    fontSize: 16,
  }),
  profilePicture: css({
    marginLeft: 5,
    marginRight: 5,
    height: 20,
    width: 20,
  }),
  favorSymbol: css({
    height: 23,
    width: 23,
    marginRight: 4,
  }),
  favorContainer: css({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'left',
    gap: 4,

    fontSize: 23,
    fontWeight: 'bold',
    marginRight: 6,
    color: UIColorPalette.playerSummaryResourceText,
  }),
  favorText: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  }),
  favorDetailContainer: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: 5,
  }),
  favorDetail: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    fontSize: 12,
    fontWeight: 'normal',
    opacity: 0.6,
    width: 30,
  }),
  favorDetailSymbol: css({
    height: 12,
    marginRight: 2,
  }),
  tokenSymbol: css({
    height: 15,
    width: 15,
  }),
  tokenContainer: css({
    display: 'flex',
    fontSize: 17,
    fontWeight: 'bold',
    justifyContent: 'space-between',
    alignItems: 'center',
  }),
} as const;

export const PlayerFavorDisplay = React.forwardRef<
  HTMLDivElement,
  {
    tributeFavor: number;
    cardFavor: number;
    eogFavor: number;
    favorMode: PlayerSummaryFavorMode;
    toggleFavorMode: () => void;
    onEnterAdditionalFavor?: () => void;
    onLeaveAdditionalFavor?: () => void;
    className?: string;
    style?: React.CSSProperties;
  }
>((props, ref) => {
  return (
    <div
      title="Favor"
      css={PlayerSummaryStyles.favorContainer}
      ref={ref}
      onClick={props.toggleFavorMode}
      className={props.className}
      style={props.style}
    >
      <div css={PlayerSummaryStyles.favorText}>
        <span>
          <SymbolView
            symbol={Symbols.FAVOR}
            css={PlayerSummaryStyles.favorSymbol}
          />
        </span>
        <span>{props.tributeFavor + props.cardFavor + props.eogFavor}</span>
      </div>
      {props.favorMode === 'detailed' && (
        <div css={PlayerSummaryStyles.favorDetailContainer}>
          <div
            title="Favor from Tributes"
            css={PlayerSummaryStyles.favorDetail}
          >
            <SymbolView
              symbol={Symbols.TOKEN_TRIBUTE}
              css={PlayerSummaryStyles.favorDetailSymbol}
            />
            {props.tributeFavor}
          </div>
          <div
            title="Base Favor from Cards"
            css={PlayerSummaryStyles.favorDetail}
          >
            <SymbolView
              symbol={Symbols.CARD_BACK}
              css={PlayerSummaryStyles.favorDetailSymbol}
            />
            {props.cardFavor}
          </div>
          <div
            title="Favor from Prophecies"
            css={PlayerSummaryStyles.favorDetail}
            onMouseEnter={props.onEnterAdditionalFavor}
            onMouseLeave={props.onLeaveAdditionalFavor}
          >
            <SymbolView
              symbol={Symbols.EOG}
              css={PlayerSummaryStyles.favorDetailSymbol}
            />
            {props.eogFavor}
          </div>
        </div>
      )}
    </div>
  );
});

export const PlayerTokenDisplay = React.forwardRef<
  HTMLDivElement,
  { token: Token; count: number }
>((props, ref) => {
  return (
    <div
      title={TokenToDisplayName[props.token]}
      css={PlayerSummaryStyles.tokenContainer}
      ref={ref}
    >
      <SymbolView
        symbol={TokenToSymbol[props.token]}
        css={PlayerSummaryStyles.tokenSymbol}
      />
      <span>{props.count}</span>
    </div>
  );
});

type PlayerResourceCounterProps = {
  resource: Resource;
  tooltip: string;
  count: number;
  generateCount: number;
  baseCount: number;
  countMode: PlayerSummaryCountMode;
  maxCount?: number;
  toggleCountMode: () => void;
  className?: string;
  style?: React.CSSProperties;
};
export const PlayerResourceCounter = React.forwardRef<
  HTMLDivElement,
  PlayerResourceCounterProps
>((props, ref) => {
  const isMaxed = props.maxCount !== undefined && props.count >= props.maxCount;
  const isNearMax =
    props.maxCount !== undefined && props.count === props.maxCount - 1;
  return (
    <div
      ref={ref}
      className={props.className}
      css={PlayerResourceStyles.container}
      style={props.style}
      onClick={props.toggleCountMode}
    >
      <SymbolView
        symbol={ResourceToSymbol[props.resource]}
        css={PlayerResourceStyles.symbol}
        title={props.tooltip}
      />
      {props.countMode === 'total' && (
        <SymbolView
          symbol={BaseResourceToSymbol[props.resource]!}
          css={PlayerResourceStyles.symbolBase}
        />
      )}
      <span
        css={[
          PlayerResourceStyles.countText,
          isMaxed && PlayerResourceStyles.maxedCountText,
          isNearMax && PlayerResourceStyles.nearMaxCountText,
        ]}
        title="Counters"
      >
        {props.count}
      </span>
      <SymbolView
        symbol={BaseResourceToSymbol[props.resource]!}
        css={[PlayerResourceStyles.symbolSmall]}
        title="Base"
      />
      <span
        css={
          props.countMode === 'counters'
            ? PlayerResourceStyles.baseText
            : PlayerResourceStyles.baseTextLight
        }
        title="Base"
      >
        {props.baseCount}
      </span>
      <SymbolView
        symbol={Symbols.EFFECT_PRODUCTION}
        css={PlayerResourceStyles.symbolProduction}
        title="Production"
      />
      <span css={PlayerResourceStyles.generateText} title="Production">
        {props.generateCount}
      </span>
    </div>
  );
});

const PlayerResourceStyles = {
  container: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    position: 'relative',
  }),
  symbol: css({
    minWidth: 15,
    minHeight: 15,
    width: 15,
    height: 15,
    marginRight: 2,
  }),
  symbolProduction: css({
    minWidth: 12,
    minHeight: 12,
    width: 12,
    height: 12,
    marginRight: 2,
  }),
  symbolSmall: css({
    minWidth: 12,
    minHeight: 12,
    width: 12,
    height: 12,
    marginRight: 2,
    filter: 'saturate(0%)',
  }),
  symbolBase: css({
    position: 'absolute',
    height: 12,
    width: 12,
    top: 6,
    left: 8,
  }),
  countContainer: css({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  }),
  countText: css({
    minWidth: 20,
    textAlign: 'right',
    marginRight: 2,
    color: UIColorPalette.playerSummaryResourceText,
  }),
  maxedCountText: css({
    color: UIColorPalette.playerSummaryResourceTextMaxed,
  }),
  nearMaxCountText: css({
    color: UIColorPalette.playerSummaryResourceTextNearMaxed,
  }),
  baseText: css({
    minWidth: 17,
    color: UIColorPalette.playerSummaryResourceText,
  }),
  baseTextLight: css({
    minWidth: 17,
    color: UIColorPalette.playerSummaryResourceTextLight,
  }),
  generateText: css({
    color: UIColorPalette.playerSummaryResourceText,
    minWidth: 17,
  }),
};

export function PlayerGameTimeView(props: {
  className?: string;
  game: GameViewModel;
  player: GamePlayerViewModel;
  isReady: boolean;
  condensed?: boolean;
}) {
  const { game, player, isReady, condensed } = props;

  let game_running = !game.gameEndTimestamp;
  let split_start_timestamp =
    !isReady && game_running ? game.phaseStartTimestamp : undefined;
  let extra_split_time =
    game_running && !condensed
      ? game.phaseThinkingMillisByPlayerID[player.userID]
      : game.totalThinkingMillisByPlayerID[player.userID];
  let extra_pre_split_time =
    game_running && !condensed
      ? game.totalThinkingMillisByPlayerID[player.userID]
      : undefined;

  return (
    <GameTimeView
      css={PlayerSummaryStyles.time}
      className={props.className}
      splitStartTimestamp={split_start_timestamp}
      extraSplitTime={extra_split_time}
      extraPreSplitTime={extra_pre_split_time}
    />
  );
}
